import { useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import type { OrderInfo } from '../../stores/klanten-store';
import type { RepeatOrderPrefill } from '../../lib/repeat-order-prefill';
import { formatDate } from '../../lib/format';

/**
 * Aanmaak-dialog voor een service-abonnement (fase 1): kies een bron-order van
 * de klant → repeat-prefill bevriest de template (kleden/pakket/adres, GEEN
 * prijzen) → interval/eerste datum/korting/comms → POST /api/abonnementen.
 * Volgt de dialog-structuur van RepeatOrderDialog.
 */
interface AbonnementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  orders: OrderInfo[];
  onCreated: () => void;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

function summarizeKleed(k: RepeatOrderPrefill['kleden'][number]): string {
  if (k.vorm === 'rechthoekig' && k.lengteCm && k.breedteCm) return `${k.lengteCm} × ${k.breedteCm} cm`;
  if (k.vorm === 'rond' && k.diameterCm) return `Ø ${k.diameterCm} cm`;
  if (k.oppervlakteM2) return `${k.oppervlakteM2} m²`;
  return k.aantal > 1 ? `× ${k.aantal}` : 'kleed';
}

export function AbonnementDialog({ isOpen, onClose, accountId, orders, onCreated }: AbonnementDialogProps) {
  const [sourceOrderId, setSourceOrderId] = useState('');
  const [prefill, setPrefill] = useState<RepeatOrderPrefill | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [naam, setNaam] = useState('');
  const [intervalWeeks, setIntervalWeeks] = useState('4');
  const [nextRunAt, setNextRunAt] = useState('');
  const [korting, setKorting] = useState('');
  const [mailsSturen, setMailsSturen] = useState(false);
  const [opmerkingen, setOpmerkingen] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const loadPrefill = async (orderId: string) => {
    setSourceOrderId(orderId);
    setPrefill(null);
    setError(null);
    if (!orderId) return;
    setPrefillLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/orders/repeat-prefill?id=${encodeURIComponent(orderId)}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.errorMessage || data.errorCode || 'Kon bron-order niet voorbereiden');
        return;
      }
      const p = data.prefill as RepeatOrderPrefill;
      setPrefill(p);
      if (!naam.trim()) setNaam(`Terugkerend: ${p.kleden.length} kleed${p.kleden.length === 1 ? '' : 'en'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setPrefillLoading(false);
    }
  };

  const canSubmit =
    !!prefill && !!naam.trim() && !!nextRunAt &&
    Number.isInteger(Number(intervalWeeks)) && Number(intervalWeeks) >= 1 && Number(intervalWeeks) <= 52 &&
    !submitting;

  const handleSubmit = async () => {
    if (!prefill) return;
    setSubmitting(true);
    setError(null);
    try {
      const kortingRaw = korting.trim().replace(',', '.');
      const kortingParsed = kortingRaw ? Number.parseFloat(kortingRaw) : NaN;
      const headers = await authHeaders();
      const res = await fetch('/api/abonnementen', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sfAccountId: accountId,
          naam: naam.trim(),
          brand: prefill.brand,
          intervalWeeks: Number(intervalWeeks),
          nextRunAt,
          template: {
            customer: {
              contactId: prefill.customer.contactId,
              firstName: prefill.customer.firstName,
              lastName: prefill.customer.lastName,
              email: prefill.customer.email,
              phone: prefill.customer.phone,
              company: prefill.customer.company,
            },
            address: prefill.address,
            packageSku: prefill.packageSku,
            kleden: prefill.kleden,
          },
          kortingPercentage: Number.isFinite(kortingParsed) && kortingParsed > 0 ? kortingParsed : null,
          bevestigingenSturen: mailsSturen,
          opmerkingenPlanning: opmerkingen.trim() || null,
          sourceWerkOrderId: prefill.sourceWerkOrderId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || 'Aanmaken mislukt');
        return;
      }
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-slate-900">Abonnement toevoegen</h2>
          <p className="text-xs text-slate-500 mt-1">
            Elke N weken automatisch een nieuwe order, tegen de dan geldende prijzen.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bron-order (Wasserij)</span>
            <select
              value={sourceOrderId}
              onChange={(e) => loadPrefill(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="">Kies een order als template…</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.reference} · {formatDate(o.createdDate)} · {o.status}
                </option>
              ))}
            </select>
            {prefillLoading && <p className="text-xs text-slate-500 mt-1">Template laden…</p>}
          </label>

          {prefill && (
            <>
              <section className="bg-slate-50 border border-slate-200 rounded px-3 py-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Template (van WO {prefill.sourceWerkOrderName})
                </h3>
                <ul className="text-sm text-slate-800 space-y-0.5">
                  {prefill.kleden.map((k, i) => (
                    <li key={i}>• {summarizeKleed(k)}{k.bijzonderheden ? ` — ${k.bijzonderheden}` : ''}</li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 mt-1">
                  {prefill.packageSku ? `Pakket: ${prefill.packageSku}` : 'Geen pakket'} · prijzen worden per
                  cyclus vers berekend.
                </p>
              </section>

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Naam</span>
                <input
                  value={naam}
                  onChange={(e) => setNaam(e.target.value)}
                  placeholder="bv. Matwissel 4-wekelijks"
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Elke … weken</span>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={intervalWeeks}
                    onChange={(e) => setIntervalWeeks(e.target.value)}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Eerste datum</span>
                  <input
                    type="date"
                    value={nextRunAt}
                    onChange={(e) => setNextRunAt(e.target.value)}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                  <span className="text-[11px] text-slate-500">Gewenste ophaal-/wisseldatum; de order wordt ~10 dagen eerder aangemaakt.</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3 items-start">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Korting %</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={korting}
                    onChange={(e) => setKorting(e.target.value)}
                    placeholder="0"
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={mailsSturen}
                    onChange={(e) => setMailsSturen(e.target.checked)}
                  />
                  <span className="text-sm text-slate-700">Klant-mails sturen</span>
                </label>
              </div>
              {!mailsSturen && (
                <p className="text-xs text-slate-500 -mt-2">
                  Uit = de klant ontvangt geen bevestigingen of reminders; planning plant de stop handmatig.
                </p>
              )}

              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Instructie voor planning</span>
                <textarea
                  rows={3}
                  value={opmerkingen}
                  onChange={(e) => setOpmerkingen(e.target.value)}
                  maxLength={1000}
                  placeholder="bv. Schone mat brengen, vuile mat ophalen — zelfde stop plannen."
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                />
              </label>
            </>
          )}

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              canSubmit ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            )}
          >
            {submitting ? 'Aanmaken…' : 'Abonnement starten'}
          </button>
        </div>
      </div>
    </div>
  );
}
